import { useEffect, useRef } from 'react'
import { updateParty } from '../../utils/partyGame'
import { DCDirectorBanner, DCHostDirectorLine } from './PartyDCIdentity'
import { IconStar } from '../icons'

/**
 * Director's Cut — Scorecard interlude.
 *
 * Shown after every Director pick (movie or actor) during gameplay.
 * Auto-advances after 4 seconds (host writes the next dcStep).
 *
 * Highlights agents who just scored with a gold flash + "+1".
 * Shows what got picked ("[Name] made the cut!").
 *
 * Props:
 *   party           — Firestore party doc
 *   currentUserId   — for "me" highlighting (optional)
 *   role            — 'host' | 'director' | 'agent'
 *   pickedLabel     — e.g. "Jaws" or "Robert Shaw"
 *   scoredUids      — array of UIDs who scored this round (for the flash)
 *   nextStep        — the dcStep this should advance to ('actor-nominate' | 'movie-nominate')
 *   nextStatus      — optional override (e.g. 'dc-results' to end the game)
 */
export default function PartyDCScoreboard({
  party, currentUserId, role, pickedLabel, scoredUids = [], nextStep, nextStatus,
}) {
  const players = party?.players ?? {}
  const directorUid = party?.directorUid
  const points = party?.agentPoints ?? {}

  // Build ranked list — agents only (Director scores nothing)
  const ranked = Object.entries(players)
    .filter(([uid]) => uid !== directorUid)
    .map(([uid, p]) => ({ uid, player: p, score: points[uid] ?? 0 }))
    .sort((a, b) => b.score - a.score)

  const isHost = role === 'host'
  const advancedRef = useRef(false)

  // Host auto-advances after 4s
  useEffect(() => {
    if (!isHost || advancedRef.current || !party?.id) return
    advancedRef.current = true
    const tid = setTimeout(() => {
      const update = { dcStep: nextStep, dcStepStartedAt: new Date(), dcLastPickedLabel: null, dcLastScoredUids: [] }
      if (nextStatus) update.status = nextStatus
      // Clear nominations for the next round
      update.dcNominations = {}
      updateParty(party.id, update).catch(() => {})
    }, 4000)
    return () => clearTimeout(tid)
  }, [isHost, party?.id, nextStep, nextStatus])

  const isDirector = role === 'director'
  return (
    <div className="dc-scoreboard">
      {isDirector && <DCDirectorBanner />}
      <p className="dc-scoreboard__title">Scorecard</p>
      {isHost && <DCHostDirectorLine party={party} />}
      {pickedLabel && (
        <p className="dc-scoreboard__picked">
          <strong>{pickedLabel}</strong> made the cut!
        </p>
      )}
      <div className="dc-scoreboard__list">
        {ranked.map(({ uid, player, score }, i) => {
          const justScored = scoredUids.includes(uid)
          const isMe = uid === currentUserId
          const initial = (player?.username || '?').charAt(0).toUpperCase()
          return (
            <div
              key={uid}
              className={[
                'dc-scoreboard__row',
                justScored ? 'dc-scoreboard__row--scored' : '',
                isMe ? 'dc-scoreboard__row--me' : '',
              ].join(' ').trim()}
            >
              <span className="dc-scoreboard__rank">#{i + 1}</span>
              <div className="dc-scoreboard__avatar">
                {player?.avatar
                  ? <img src={player.avatar} alt="" />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>
              <span className="dc-scoreboard__name">
                {player?.username || 'Guest'}{isMe ? ' (you)' : ''}
              </span>
              <span className="dc-scoreboard__score">
                <IconStar size={14} aria-hidden="true" />
                <span>{score}</span>
              </span>
              {justScored && <span className="dc-scoreboard__pop">+1</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
